<template>
    <div
        v-if="isOpen"
        ref="dialogRef"
        class="relative z-98"
        aria-labelledby="slide-in-title"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
    >
        <!-- Backdrop -->
        <transition
            enter-active-class="ease-in-out duration-500"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in-out duration-500"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isOpen"
                class="fixed inset-0 bg-taupe-900/50 transition-opacity"
            ></div>
        </transition>

        <!-- Slide-in panel -->
        <div class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <div
                    class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10"
                >
                    <transition
                        enter-active-class="transform transition ease-out duration-700"
                        enter-from-class="translate-x-full opacity-0"
                        enter-to-class="translate-x-0 opacity-100"
                        leave-active-class="transform transition ease-in duration-500"
                        leave-from-class="translate-x-0 opacity-100"
                        leave-to-class="translate-x-full opacity-0"
                    >
                        <div
                            v-if="isOpen"
                            class="pointer-events-auto w-screen max-w-md"
                            @click.stop
                        >
                            <div
                                class="flex h-full flex-col bg-white shadow-xl rounded-l-xl"
                            >
                                <!-- Header -->
                                <div
                                    class="bg-sky-500 px-4 py-4 sm:px-6 rounded-tl-xl flex-shrink-0"
                                >
                                    <div
                                        class="flex items-center justify-between"
                                    >
                                        <h2
                                            class="text-lg font-medium leading-6 text-white"
                                            id="slide-in-title"
                                        >
                                            {{ title }}
                                        </h2>
                                        <div class="ml-3 flex h-7 items-center">
                                            <button
                                                type="button"
                                                class="relative rounded-md bg-sky-500 text-sky-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                @click="$emit('close')"
                                            >
                                                <span
                                                    class="absolute -inset-2.5"
                                                ></span>
                                                <span class="sr-only"
                                                    >Close panel</span
                                                >
                                                <zen-icon-close
                                                    class="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="error" class="mt-1">
                                        <zen-message-block type="error">
                                            {{ error }}
                                        </zen-message-block>
                                    </div>
                                </div>

                                <!-- Icon slot -->
                                <div
                                    v-if="$slots.icon"
                                    class="px-4 pt-6 pb-3 sm:px-6 flex-shrink-0"
                                >
                                    <slot name="icon"></slot>
                                </div>

                                <!-- Body content -->
                                <div
                                    class="relative flex-1 px-4 pt-3 pb-6 sm:px-6 overflow-y-auto"
                                >
                                    <slot name="body">
                                        <slot></slot>
                                    </slot>
                                </div>

                                <!-- Buttons -->
                                <div
                                    v-if="$slots.buttons"
                                    class="flex flex-shrink-0 justify-end gap-x-3 border-t border-taupe-200 px-4 py-4 bg-white sticky bottom-0"
                                >
                                    <slot name="buttons"></slot>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useFocusTrap } from '@/composables/useFocusTrap'

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    error: {
        type: String,
        default: '',
    },
    isOpen: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close'])

const dialogRef = ref(null)
useFocusTrap(dialogRef, () => props.isOpen)

function onKeydown(event) {
    if (event.key === 'Escape' && props.isOpen) {
        emit('close')
    }
}

onMounted(() => {
    document.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown)
})
</script>
